// src/components/item-builder/DragToGroupModal.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { Plus, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

// فرم خالی برای حالت ایجاد
const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  groups: [
    { key: "G1", fa: "", en: "" },
    { key: "G2", fa: "", en: "" },
  ],
  items: [
    { key: "I1", fa: "", en: "", groupKey: "G1" },
    { key: "I2", fa: "", en: "", groupKey: "G2" },
  ],
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

// تبدیل آیتم بک‌اند → فرم برای ادیت
const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};

  const rawGroups = Array.isArray(body.groups) ? body.groups : [];
  const groups =
    rawGroups.length > 0
      ? rawGroups.map((g, idx) => ({
          key: g.key || `G${idx + 1}`,
          fa: g?.label?.fa || "",
          en: g?.label?.en || "",
        }))
      : makeEmptyForm().groups;

  const rawItems = Array.isArray(body.items) ? body.items : [];
  const items =
    rawItems.length > 0
      ? rawItems.map((it, idx) => ({
          key: it.key || `I${idx + 1}`,
          fa: it?.label?.fa || "",
          en: it?.label?.en || "",
          groupKey: it.groupKey || groups[0]?.key || "G1",
        }))
      : makeEmptyForm().items;

  const layout = body.layout || "top-bottom";
  const points = item?.points ?? 1;
  const status = item?.status || "published";
  const tags = Array.isArray(item?.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    groups,
    items,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function DragToGroupModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) setForm(itemToForm(item));
    else setForm(makeEmptyForm());
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const addGroup = () =>
    setForm((f) => ({
      ...f,
      groups: [...f.groups, { key: `G${f.groups.length + 1}`, fa: "", en: "" }],
    }));

  const removeGroup = (i) =>
    setForm((f) => {
      if (f.groups.length <= 2) return f; // حداقل ۲ گروه
      const groups = f.groups.filter((_, idx) => idx !== i);
      return { ...f, groups };
    });

  const addItem = () =>
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          key: `I${f.items.length + 1}`,
          fa: "",
          en: "",
          groupKey: f.groups[0]?.key || "G1",
        },
      ],
    }));

  const removeItem = (i) =>
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i),
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("drag_to_group.toasts.stem_required"));
      return;
    }
    if (form.groups.length < 2) {
      toast.error(t("drag_to_group.toasts.need_two_groups"));
      return;
    }
    if (form.items.length < 1) {
      toast.error(t("drag_to_group.toasts.need_items"));
      return;
    }

    const payload = {
      kind: "drag_to_group",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: { fa: form.stem_fa || undefined, en: form.stem_en || undefined },
      body: {
        groups: form.groups.map((g) => ({
          key: g.key,
          label: { fa: g.fa || undefined, en: g.en || undefined },
        })),
        items: form.items.map((it) => ({
          key: it.key,
          label: { fa: it.fa || undefined, en: it.en || undefined },
          groupKey: it.groupKey || undefined,
        })),
        layout: form.layout,
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t(
              "drag_to_group.toasts.updated",
              "سؤال Drag to Group با موفقیت ویرایش شد"
            )
          : t(
              "drag_to_group.toasts.created",
              "سؤال Drag to Group با موفقیت ساخته شد"
            )
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("drag_to_group.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("drag_to_group.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("drag_to_group.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("drag_to_group.stem_en_ph")}
          />
        </div>
      </div>

      {/* Layout Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("drag_to_group.layout")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "right-left", "left-right"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, layout: e.target.value }))
                  }
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`drag_to_group.layout_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>

      {/* Groups */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("drag_to_group.groups")}
          </label>
          <button
            type="button"
            onClick={addGroup}
            className="text-blue-600 text-sm"
          >
            <Plus size={16} className="inline-block mr-1" />
            {t("drag_to_group.add_group")}
          </button>
        </div>
        <div className="space-y-2">
          {form.groups.map((g, gi) => (
            <div
              key={g.key || gi}
              className="grid grid-cols-1 md:grid-cols-3 gap-2"
            >
              <input
                className="border rounded-lg px-3 py-2"
                placeholder={t("drag_to_group.group_key_ph")}
                value={g.key}
                onChange={(e) => {
                  const groups = [...form.groups];
                  groups[gi].key = e.target.value;
                  setForm((f) => ({ ...f, groups }));
                }}
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder={t("drag_to_group.group_fa_ph")}
                value={g.fa}
                onChange={(e) => {
                  const groups = [...form.groups];
                  groups[gi].fa = e.target.value;
                  setForm((f) => ({ ...f, groups }));
                }}
              />
              <div className="flex gap-2">
                <input
                  className="border rounded-lg px-3 py-2 flex-1"
                  placeholder={t("drag_to_group.group_en_ph")}
                  value={g.en}
                  onChange={(e) => {
                    const groups = [...form.groups];
                    groups[gi].en = e.target.value;
                    setForm((f) => ({ ...f, groups }));
                  }}
                />
                {form.groups.length > 2 && (
                  <button
                    type="button"
                    className="px-2 rounded border text-red-600"
                    onClick={() => removeGroup(gi)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Items */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("drag_to_group.items")}
          </label>
          <button
            type="button"
            onClick={addItem}
            className="text-blue-600 text-sm"
          >
            <Plus size={16} className="inline-block mr-1" />
            {t("drag_to_group.add_item")}
          </button>
        </div>
        <div className="space-y-2">
          {form.items.map((it, idx) => (
            <div
              key={it.key || idx}
              className="grid grid-cols-1 md:grid-cols-4 gap-2"
            >
              <input
                className="border rounded-lg px-3 py-2"
                placeholder={t("drag_to_group.item_key_ph")}
                value={it.key}
                onChange={(e) => {
                  const items = [...form.items];
                  items[idx].key = e.target.value;
                  setForm((f) => ({ ...f, items }));
                }}
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder={t("drag_to_group.item_fa_ph")}
                value={it.fa}
                onChange={(e) => {
                  const items = [...form.items];
                  items[idx].fa = e.target.value;
                  setForm((f) => ({ ...f, items }));
                }}
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder={t("drag_to_group.item_en_ph")}
                value={it.en}
                onChange={(e) => {
                  const items = [...form.items];
                  items[idx].en = e.target.value;
                  setForm((f) => ({ ...f, items }));
                }}
              />
              <div className="flex gap-2">
                <select
                  className="border rounded-lg px-3 py-2 flex-1"
                  value={it.groupKey}
                  onChange={(e) => {
                    const items = [...form.items];
                    items[idx].groupKey = e.target.value;
                    setForm((f) => ({ ...f, items }));
                  }}
                >
                  {form.groups.map((g) => (
                    <option key={g.key} value={g.key}>
                      {g.key} — {g.fa || g.en || t("drag_to_group.group_label")}
                    </option>
                  ))}
                </select>
                {form.items.length > 1 && (
                  <button
                    type="button"
                    className="px-2 rounded border text-red-600"
                    onClick={() => removeItem(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* bottom */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("drag_to_group.points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("drag_to_group.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("drag_to_group.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>
    </form>
  );
}
